import { ptBR } from 'date-fns/locale';

import { formatDateWithPattern } from '@/utils/date-time/formatDateWithPattern';
import {
  ResolveThreatSituationTagProps,
  StatusOptionProps,
} from './model';

const THREAT_DATETIME_PATTERN = 'dd/MM/yyyy, HH:mm:ss';

const DEFAULT_MOBILE_PATH_DISPLAY_LENGTH = 35;

/**
 * Shortens long file paths for compact mobile threat cards.
 */
export const ellipsisThreatPathForMobile = (
  path: string,
  maxLength = DEFAULT_MOBILE_PATH_DISPLAY_LENGTH,
): string => {
  if (path.length <= maxLength) {
    return path;
  }
  return `${path.slice(0, maxLength)}...`;
};

/**
 * Formats an ISO timestamp for the threats table (`pt-BR`-style date + time).
 * Falls back to the raw `iso` string when parsing/formatting fails.
 */
export const formatThreatDateTime = (iso: string): string => {
  const formatted = formatDateWithPattern(iso, THREAT_DATETIME_PATTERN, { locale: ptBR });
  return formatted || iso;
};

const THREAT_STATUS = {
  quarantined: 'quarantined',
  cleaned: 'cleaned',
  detected: 'detected',
} as const;

/**
 * Maps backend `status` to a `Tag` label + variant (quarantine → pending, remediated/clean/resolved → success).
 * Known states use `labels` from locale; unknown values use `variant: 'neutral'` and the raw `status` as label.
 */
export const resolveThreatSituationTag = ({
  status,
  tagsLocale,
}: ResolveThreatSituationTagProps) => {
  const normalizedStatus = status?.toLowerCase() as StatusOptionProps;

  const rules: Record<StatusOptionProps, { label: string; variant: 'pending' | 'success' }> = {
    [THREAT_STATUS.detected]: { label: tagsLocale.detected, variant: 'pending' },
    [THREAT_STATUS.quarantined]: { label: tagsLocale.remediated, variant: 'success' },
    [THREAT_STATUS.cleaned]: { label: tagsLocale.remediated, variant: 'success' },
  };

  return rules[normalizedStatus] ?? { label: status, variant: 'neutral' };
};
